/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Claude Code 2026-09-21: Phase 4 Sidebar Anchors
 * Sidebar container for anchored apps during presentations
 */

import { Box, useColorModeValue, VStack, Text, Badge, useToast } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { MdBookmark } from 'react-icons/md';

import { useAppStore, useUIStore } from '@sage3/frontend';
import { layoutAnchoredApps, sidebarWidthInBoardUnits, checkAnchorLimit } from '@sage3/applications/utils';
import { SidebarApp } from './SidebarApp';

interface SidebarAnchorsProps {
  anchoredAppIds: string[]; // IDs of apps to show in sidebar
  sidebarWidthPercent: number; // Sidebar width as percentage (default: 15)
  position: 'left' | 'right'; // Sidebar position (default: 'left')
}

/**
 * Sidebar container that displays anchored apps during presentations
 *
 * Features:
 * - Auto-layout apps vertically with equal spacing
 * - Fixed position (left or right edge)
 * - Stays visible during presentation navigation
 * - Apps are fully interactive
 */
export function SidebarAnchors({
  anchoredAppIds,
  sidebarWidthPercent = 15,
  position = 'left'
}: SidebarAnchorsProps) {
  // Get all apps from store
  const apps = useAppStore((state) => state.apps);
  const toast = useToast();

  // Filter to anchored apps only
  const anchoredApps = useMemo(() => {
    return apps.filter(app => anchoredAppIds.includes(app._id));
  }, [apps, anchoredAppIds]);

  // Check anchor limit and show warning
  useEffect(() => {
    const warning = checkAnchorLimit(anchoredApps.length);
    if (warning) {
      console.warn(`Sidebar: ${warning}`);
      toast({
        title: 'Too Many Anchored Apps',
        description: warning,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  }, [anchoredApps.length, toast]);

  // Calculate sidebar dimensions in VIEWPORT PIXELS (not board units)
  const sidebarWidthPixels = (sidebarWidthPercent / 100) * window.innerWidth;
  const sidebarHeightPixels = window.innerHeight;

  // Layout apps
  const layoutItems = useMemo(() => {
    return layoutAnchoredApps(anchoredApps, {
      sidebarWidth: sidebarWidthPixels,
      sidebarHeight: sidebarHeightPixels,
      spacing: 20,
      minAppHeight: 100
    });
  }, [anchoredApps, sidebarWidthPixels, sidebarHeightPixels]);

  // Color mode
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const headerBgColor = useColorModeValue('gray.100', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  // Don't render if no anchored apps
  if (anchoredApps.length === 0) {
    return null;
  }

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      bg={bgColor}
      borderRight={position === 'left' ? '2px solid' : undefined}
      borderLeft={position === 'right' ? '2px solid' : undefined}
      borderColor={borderColor}
      overflow="hidden"
    >
      {/* Header */}
      <Box
        bg={headerBgColor}
        px={3}
        py={2}
        borderBottom="1px solid"
        borderColor={borderColor}
      >
        <VStack align="start" spacing={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <MdBookmark size={16} color="var(--chakra-colors-green-500)" />
            <Text fontSize="sm" fontWeight="bold" color={textColor}>
              Anchored
            </Text>
            <Badge colorScheme="green" fontSize="xs">
              {anchoredApps.length}
            </Badge>
          </Box>
          <Text fontSize="xs" color={textColor} opacity={0.7}>
            Stays visible during presentation
          </Text>
        </VStack>
      </Box>

      {/* Apps container */}
      <Box position="relative" width="100%" height="calc(100% - 60px)">
        {layoutItems.map((item) => (
          <SidebarApp
            key={item.appId}
            app={item.app}
            position={item.position}
            size={item.size}
          />
        ))}
      </Box>
    </Box>
  );
}
